
import React from 'react';
import LandingHeader from "@/components/LandingHeader";
import LandingFooter from "@/components/LandingFooter";

const PrivacyPolicy = () => {
  const lastUpdated = new Date("2023-08-15").toLocaleDateString();

  return (
    <div className="min-h-screen bg-pastel-blue-light dark:bg-[#121624] text-custom-dark dark:text-gray-100">
      <LandingHeader />
      <main className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Politique de Confidentialité</h1>
          
          <p className="text-sm text-gray-500 mb-8">
            Dernière mise à jour: {lastUpdated}
          </p>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="mb-4">
              Chez ScalooAI ("nous", "notre", "nos"), nous nous engageons à protéger votre vie privée. Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations personnelles lorsque vous utilisez notre site web et nos services (collectivement, les "Services").
            </p>
            <p className="mb-4">
              En utilisant nos Services, vous acceptez les pratiques décrites dans cette Politique de Confidentialité. Si vous n'acceptez pas cette politique, veuillez ne pas utiliser nos Services.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Responsable du Traitement</h2>
            <p className="mb-4">
              ScalooAI est le responsable du traitement de vos données personnelles au sens du Règlement Général sur la Protection des Données (RGPD) et d'autres lois applicables sur la protection des données.
            </p>
            <p className="mb-4">
              Pour toute question concernant cette Politique de Confidentialité ou vos données, veuillez nous contacter à: privacy@scalooai.com
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. Informations que Nous Collectons</h2>
            <p className="mb-4">
              Nous collectons différents types d'informations personnelles, notamment:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Informations de compte:</strong> nom, adresse email, mot de passe et autres détails fournis lors de la création d'un compte.</li>
              <li><strong>Informations de profil:</strong> photo, biographie et préférences que vous choisissez de partager.</li>
              <li><strong>Informations d'utilisation:</strong> données sur la façon dont vous interagissez avec nos Services, y compris les fonctionnalités utilisées et les actions effectuées.</li>
              <li><strong>Informations sur l'appareil:</strong> type d'appareil, système d'exploitation, navigateur, adresse IP et identifiants uniques.</li>
              <li><strong>Informations de paiement:</strong> détails nécessaires pour traiter les transactions (notez que nous ne stockons pas les informations complètes de carte de crédit).</li>
              <li><strong>Contenu utilisateur:</strong> tout contenu que vous créez, téléchargez ou partagez en utilisant nos Services.</li>
              <li><strong>Informations de tiers:</strong> données obtenues via l'intégration avec des services tiers (avec votre autorisation).</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Base Juridique du Traitement</h2>
            <p className="mb-4">
              Nous traitons vos données personnelles sur les bases juridiques suivantes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Exécution du contrat:</strong> pour vous fournir nos Services conformément à nos Conditions d'Utilisation.</li>
              <li><strong>Consentement:</strong> lorsque vous avez expressément consenti au traitement.</li>
              <li><strong>Intérêts légitimes:</strong> lorsque nécessaire pour nos intérêts légitimes, sans porter atteinte à vos droits et libertés.</li>
              <li><strong>Obligation légale:</strong> lorsque requis par la loi.</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. Utilisation de Vos Informations</h2>
            <p className="mb-4">
              Nous utilisons vos informations personnelles pour:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Fournir, maintenir et améliorer nos Services.</li>
              <li>Créer et gérer votre compte.</li>
              <li>Traiter vos transactions et abonnements.</li>
              <li>Personnaliser votre expérience et vous présenter des offres pertinentes.</li>
              <li>Communiquer avec vous concernant votre compte, nos services, ou pour répondre à vos demandes.</li>
              <li>Envoyer des communications marketing (avec votre consentement lorsque requis par la loi).</li>
              <li>Analyser l'utilisation de nos Services pour améliorer nos offres.</li>
              <li>Détecter, prévenir et résoudre les problèmes techniques ou de sécurité.</li>
              <li>Se conformer aux obligations légales.</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Partage et Divulgation</h2>
            <p className="mb-4">
              Nous ne vendons pas vos données personnelles. Nous pouvons partager vos informations dans les circonstances suivantes:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Fournisseurs de services:</strong> avec des tiers qui nous aident à fournir nos Services (traitement des paiements, analyse de données, support client, etc.).</li>
              <li><strong>Partenaires commerciaux:</strong> lorsque vous avez explicitement consenti à ce partage.</li>
              <li><strong>Conformité légale:</strong> lorsque nous sommes légalement tenus de divulguer vos informations.</li>
              <li><strong>Protection des droits:</strong> pour protéger nos droits, notre propriété ou notre sécurité, ainsi que ceux de nos utilisateurs.</li>
              <li><strong>Transactions d'entreprise:</strong> dans le cadre d'une fusion, acquisition, vente d'actifs ou faillite.</li>
            </ul>
            <p className="mb-4">
              Tous nos fournisseurs de services sont tenus de respecter des obligations de confidentialité et de sécurité appropriées.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Transferts Internationaux de Données</h2>
            <p className="mb-4">
              Nous pouvons traiter vos informations personnelles dans différents pays, y compris en dehors de votre pays de résidence. Les lois sur la protection des données varient selon les juridictions.
            </p>
            <p className="mb-4">
              Lorsque nous transférons des données en dehors de l'Espace Économique Européen (EEE) ou d'autres régions avec des lois strictes sur la protection des données, nous nous assurons que des protections adéquates sont en place, conformément aux exigences légales, comme les clauses contractuelles types approuvées par la Commission européenne.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Conservation des Données</h2>
            <p className="mb-4">
              Nous conservons vos données personnelles aussi longtemps que nécessaire pour les finalités décrites dans cette politique, ou pour respecter nos obligations légales. Les critères utilisés pour déterminer nos périodes de conservation incluent:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>La durée pendant laquelle vous utilisez nos Services.</li>
              <li>Si nous avons une obligation légale de conserver les données.</li>
              <li>Si la conservation est conseillée compte tenu de notre position juridique (délais de prescription, litiges, etc.).</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Vos Droits</h2>
            <p className="mb-4">
              Selon votre lieu de résidence, vous pouvez disposer des droits suivants concernant vos données personnelles:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li><strong>Droit d'accès:</strong> obtenir confirmation du traitement de vos données et accéder à vos informations personnelles.</li>
              <li><strong>Droit de rectification:</strong> corriger des informations inexactes ou incomplètes.</li>
              <li><strong>Droit à l'effacement:</strong> demander la suppression de vos données dans certaines circonstances.</li>
              <li><strong>Droit à la limitation du traitement:</strong> demander la restriction du traitement dans certaines circonstances.</li>
              <li><strong>Droit à la portabilité des données:</strong> recevoir vos données dans un format structuré et les transférer à un autre responsable du traitement.</li>
              <li><strong>Droit d'opposition:</strong> vous opposer au traitement fondé sur nos intérêts légitimes ou à des fins de marketing direct.</li>
              <li><strong>Droit de retirer votre consentement:</strong> retirer votre consentement à tout moment lorsque le traitement est basé sur celui-ci.</li>
              <li><strong>Droit de déposer une plainte:</strong> auprès d'une autorité de protection des données.</li>
            </ul>
            <p className="mb-4">
              Pour exercer ces droits, veuillez nous contacter à privacy@scalooai.com. Nous répondrons à votre demande dans les délais prévus par la loi applicable.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Sécurité des Données</h2>
            <p className="mb-4">
              Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre la perte, l'accès non autorisé, la divulgation, l'altération ou la destruction. Ces mesures incluent le chiffrement, les contrôles d'accès, et les audits de sécurité réguliers.
            </p>
            <p className="mb-4">
              Cependant, aucun système de sécurité n'est impénétrable. Si vous avez des raisons de croire que votre interaction avec nous n'est plus sécurisée, veuillez nous contacter immédiatement.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Cookies et Technologies Similaires</h2>
            <p className="mb-4">
              Nous utilisons des cookies et des technologies similaires pour collecter des informations sur votre utilisation de nos Services, personnaliser votre expérience, et pour d'autres fins décrites dans notre Politique de Cookies.
            </p>
            <p className="mb-4">
              Vous pouvez configurer votre navigateur pour refuser tous les cookies ou pour indiquer quand un cookie est envoyé. Cependant, certaines fonctionnalités de nos Services peuvent ne pas fonctionner correctement sans cookies.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Vie Privée des Enfants</h2>
            <p className="mb-4">
              Nos Services ne s'adressent pas aux personnes de moins de 16 ans et nous ne collectons pas sciemment des informations personnelles d'enfants de moins de 16 ans. Si vous êtes un parent ou un tuteur et que vous pensez que votre enfant nous a fourni des informations personnelles, veuillez nous contacter.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Modifications de cette Politique</h2>
            <p className="mb-4">
              Nous pouvons mettre à jour cette Politique de Confidentialité périodiquement. La version la plus récente sera toujours disponible sur notre site web, avec la date de la dernière révision.
            </p>
            <p className="mb-4">
              Pour les modifications importantes, nous vous fournirons un préavis approprié, comme l'affichage d'un avis visible sur notre site web ou en vous envoyant une notification directe.
            </p>
            <p className="mb-4">
              Votre utilisation continue de nos Services après la publication d'une Politique de Confidentialité révisée signifie que vous acceptez les modifications. Nous vous encourageons à consulter régulièrement cette politique.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Contact</h2>
            <p className="mb-4">
              Si vous avez des questions, préoccupations ou demandes concernant cette Politique de Confidentialité ou nos pratiques en matière de protection des données, veuillez nous contacter à:
            </p>
            <div className="mb-4 pl-6">
              <p>Email: privacy@scalooai.com</p>
              <p>Adresse postale: [Adresse de l'entreprise]</p>
              <p>Téléphone: [Numéro de téléphone]</p>
            </div>
            <p className="mb-4">
              Nous nous efforcerons de répondre à votre demande dans les meilleurs délais et conformément aux lois applicables.
            </p>
          </section>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
};

export default PrivacyPolicy;
